import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Card from './ui/Card';
import { ButtonRow, GoldButton, OutlineButton } from './ui/Buttons';
import { ui } from './ui/tokens';
import { getParameters } from '../libs/BitmapEditor/bitmapMaker3';
import { getCreateWith } from '../helpers/createWith';

// Extend Window interface to include displaySketch
declare global {
	interface Window {
		displaySketch: any;
	}
}

export type SaveModalProps = {
	open: boolean;
	onClose: () => void;
};

// Social platforms display images at ~1080px and upscale smaller ones with
// smoothing, which blurs pixel art — so exports are upscaled here first,
// nearest-neighbour, at the sizes the platforms actually use.
const EXPORT_TARGET_PX = 1080;
const STORY_HEIGHT_PX = 1920;
const CREDIT_TEXT = 'made with createwithpixels';
// Below this export width the credit text cannot be drawn readably (26 chars
// of >=9px monospace) — the credit option is disabled instead of clipping
const MIN_CREDIT_WIDTH_PX = 160;

type PresetKey = 'original' | 'large' | 'square' | 'story' | 'custom';

const PRESETS: { key: PresetKey; label: string; hint: string }[] = [
	{ key: 'original', label: 'Original', hint: 'Exact pixels — smallest file.' },
	{ key: 'large', label: 'Large', hint: 'Sharp upscale to around 1080px — good everywhere.' },
	{ key: 'square', label: 'Square post', hint: '1080 × 1080 — Instagram and other feed posts.' },
	{ key: 'story', label: 'Story', hint: '1080 × 1920 — Stories, TikTok and WhatsApp status.' },
	{ key: 'custom', label: 'Custom', hint: 'Pick your own scale factor.' },
];

type ExportLayout = {
	canvasWidth: number;
	canvasHeight: number;
	artWidth: number;
	artHeight: number;
	offsetX: number;
	offsetY: number;
	scale: number;
	stripHeight: number;
};

function computeLayout(baseWidth: number, baseHeight: number, preset: PresetKey, customScale: number, withCredit: boolean): ExportLayout {
	const maxSide = Math.max(baseWidth, baseHeight);
	if (preset === 'square' || preset === 'story') {
		const canvasWidth = EXPORT_TARGET_PX;
		const canvasHeight = preset === 'square' ? EXPORT_TARGET_PX : STORY_HEIGHT_PX;
		const stripHeight = withCredit && canvasWidth >= MIN_CREDIT_WIDTH_PX ? creditStripHeight(canvasWidth) : 0;
		// Integer scale keeps pixel columns even; floor so the art always fits
		const scale = Math.max(1, Math.floor(Math.min(canvasWidth / baseWidth, (canvasHeight - stripHeight) / baseHeight)));
		const artWidth = baseWidth * scale;
		const artHeight = baseHeight * scale;
		return {
			canvasWidth,
			canvasHeight,
			artWidth,
			artHeight,
			offsetX: Math.round((canvasWidth - artWidth) / 2),
			offsetY: Math.round((canvasHeight - stripHeight - artHeight) / 2),
			scale,
			stripHeight,
		};
	}
	const scale = preset === 'original' ? 1 : preset === 'large' ? Math.max(1, Math.round(EXPORT_TARGET_PX / maxSide)) : customScale;
	const artWidth = Math.round(baseWidth * scale);
	const artHeight = Math.round(baseHeight * scale);
	const stripHeight = withCredit && artWidth >= MIN_CREDIT_WIDTH_PX ? creditStripHeight(artWidth) : 0;
	return {
		canvasWidth: artWidth,
		canvasHeight: artHeight + stripHeight,
		artWidth,
		artHeight,
		offsetX: 0,
		offsetY: 0,
		scale,
		stripHeight,
	};
}

function creditStripHeight(canvasWidth: number): number {
	return Math.max(18, Math.round(canvasWidth * 0.055));
}

function getCanvasBackgroundHex(): string {
	const hex = (window as any).editSketch?.getBackgroundColourHex?.();
	return typeof hex === 'string' && hex.length ? hex : '#000000';
}

function drawCreditStrip(ctx: CanvasRenderingContext2D, layout: ExportLayout) {
	const { canvasWidth, canvasHeight, stripHeight } = layout;
	ctx.fillStyle = '#222222';
	ctx.fillRect(0, canvasHeight - stripHeight, canvasWidth, stripHeight);
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	// Shrink-to-fit so the credit never overflows narrow exports
	let fontSize = Math.floor(stripHeight * 0.55);
	ctx.font = `600 ${fontSize}px 'Courier New', monospace`;
	while (fontSize > 5 && ctx.measureText(CREDIT_TEXT).width > canvasWidth * 0.92) {
		fontSize--;
		ctx.font = `600 ${fontSize}px 'Courier New', monospace`;
	}
	if (ctx.measureText(CREDIT_TEXT).width > canvasWidth * 0.92) {
		// Cannot fit readably even at the minimum size (layout should prevent
		// this via MIN_CREDIT_WIDTH_PX) — skip rather than draw clipped glyphs
		return;
	}
	ctx.fillStyle = '#e5b800';
	ctx.fillText(CREDIT_TEXT, canvasWidth / 2, canvasHeight - stripHeight / 2 + 1);
}

function SaveModal({ open, onClose }: SaveModalProps) {
	const [preset, setPreset] = useState<PresetKey>('large');
	const [customScale, setCustomScale] = useState<string>('');
	const [withCredit, setWithCredit] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const createWith = getCreateWith();
	const dpSketch = () => window.displaySketch;

	// Get canvas dimensions from parameters
	const parameters = getParameters();
	const baseWidth = parameters.width;
	const baseHeight = parameters.height;

	const scaleInput = customScale === '' ? 0 : parseFloat(customScale) || 1;
	const layout = computeLayout(baseWidth, baseHeight, preset, scaleInput, withCredit);
	const presetHint = PRESETS.find((p) => p.key === preset)?.hint ?? '';
	// canvasWidth does not depend on withCredit, so this is not circular
	const creditTooNarrow = layout.canvasWidth < MIN_CREDIT_WIDTH_PX;

	// Reset state when modal opens
	useEffect(() => {
		if (open) {
			setPreset('large');
			setCustomScale('');
			setWithCredit(false);
			setIsSaving(false);
		}
	}, [open]);

	const handlePresetSelect = (key: PresetKey) => {
		setPreset(key);
		if (key === 'custom' && customScale === '') {
			setCustomScale('1');
		}
	};

	const handleCustomScaleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		// Allow empty string or positive numbers with up to 2 decimal places
		if (value === '' || (/^\d*\.?\d{0,2}$/.test(value) && (value === '' || parseFloat(value) <= 20))) {
			setCustomScale(value);
		}
	};

	const generateExportCanvas = (): HTMLCanvasElement => {
		const sourceCanvas = dpSketch()?.canvas as HTMLCanvasElement;
		if (!sourceCanvas) {
			throw new Error('Source canvas not found');
		}

		const canvas = document.createElement('canvas');
		canvas.width = layout.canvasWidth;
		canvas.height = layout.canvasHeight;

		const ctx = canvas.getContext('2d');
		if (!ctx) {
			throw new Error('Could not get 2D context');
		}

		// Disable image smoothing for pixel-perfect scaling
		ctx.imageSmoothingEnabled = false;
		// @ts-ignore - Webkit prefix
		ctx.webkitImageSmoothingEnabled = false;
		// @ts-ignore - Mozilla prefix
		ctx.mozImageSmoothingEnabled = false;
		// @ts-ignore - Microsoft prefix
		ctx.msImageSmoothingEnabled = false;

		// Letterbox and credit areas match the canvas background so the export looks seamless
		ctx.fillStyle = getCanvasBackgroundHex();
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		ctx.drawImage(sourceCanvas, 0, 0, baseWidth, baseHeight, layout.offsetX, layout.offsetY, layout.artWidth, layout.artHeight);

		if (withCredit && layout.stripHeight > 0) {
			drawCreditStrip(ctx, layout);
		}

		return canvas;
	};

	const handleSave = async () => {
		if (!createWith || isSaving) return;

		try {
			setIsSaving(true);

			const exportCanvas = generateExportCanvas();
			const canvasData = exportCanvas.toDataURL('image/png');

			await createWith.local_save(canvasData);
			onClose();
		} catch (error: any) {
			console.error('Error saving scaled image:', error);
			alert('Error saving image to local device.');
		} finally {
			setIsSaving(false);
		}
	};

	const isValidScale = preset !== 'custom' || (scaleInput > 0 && scaleInput <= 20 && customScale !== '');

	return (
		<Card open={open} title="Save to device" onClose={isSaving ? undefined : onClose}>
			<Body>
				<Muted>
					Original size: {baseWidth} x {baseHeight} pixels
				</Muted>

				<Heading>Export size</Heading>
				<PresetGrid role="radiogroup" aria-label="Export size">
					{PRESETS.map(({ key, label }) => (
						<PresetChip key={key} type="button" role="radio" aria-checked={preset === key} $selected={preset === key} onClick={() => handlePresetSelect(key)}>
							{label}
						</PresetChip>
					))}
				</PresetGrid>
				<Muted style={{ marginTop: 8 }}>{presetHint}</Muted>

				{preset === 'custom' && (
					<CustomRow>
						<ScaleInput value={customScale} onChange={handleCustomScaleChange} placeholder="1.5" inputMode="decimal" aria-label="Custom scale" $invalid={customScale !== '' && !isValidScale} />
						<Muted>x scale (0.1 - 20)</Muted>
					</CustomRow>
				)}

				<CreditRow $disabled={creditTooNarrow}>
					<input type="checkbox" checked={withCredit && !creditTooNarrow} disabled={creditTooNarrow} onChange={event => setWithCredit(event.target.checked)} />
					<span>Add a small "{CREDIT_TEXT}" credit strip</span>
				</CreditRow>
				{creditTooNarrow && <Muted>This export is too narrow for the credit. Pick a larger size.</Muted>}

				<Heading>Final dimensions</Heading>
				{preset === 'custom' && customScale === '' ? (
					<Muted>Enter a scale to see the dimensions</Muted>
				) : (
					<>
						<Final>
							{layout.canvasWidth} x {layout.canvasHeight} pixels
						</Final>
						<Muted>{layout.scale}x scale factor</Muted>
					</>
				)}

				<ButtonRow>
					<OutlineButton type="button" onClick={onClose} disabled={isSaving}>
						Cancel
					</OutlineButton>
					<GoldButton type="button" onClick={handleSave} disabled={!isValidScale || isSaving}>
						{isSaving ? 'Saving...' : 'Save image'}
					</GoldButton>
				</ButtonRow>
			</Body>
		</Card>
	);
}

export default SaveModal;

const Body = styled.div`
	margin-top: 14px;
	font-family: ${ui.font};
	color: ${ui.text};
`;

const Heading = styled.h3`
	margin: 20px 0 10px;
	font-size: 16px;
	font-weight: 600;
`;

const Muted = styled.div`
	font-size: 12px;
	line-height: 1.4;
	color: ${ui.label};
`;

const PresetGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
`;

// Style guide R16: unselected options are solid gold, the selected one inverts
const PresetChip = styled.button<{ $selected: boolean }>`
	padding: 9px 12px;
	border-radius: 4px;
	border: 1px solid ${ui.gold};
	background-color: ${props => (props.$selected ? '#000000' : ui.gold)};
	color: ${props => (props.$selected ? ui.gold : ui.onGold)};
	font-family: ${ui.font};
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
`;

const CustomRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: 12px;
`;

const ScaleInput = styled.input<{ $invalid: boolean }>`
	width: 90px;
	height: 44px;
	border-radius: 10px;
	border: 1px solid ${props => (props.$invalid ? ui.error : ui.gold)};
	background: transparent;
	color: ${ui.text};
	font-family: ${ui.font};
	font-size: 16px;
	text-align: center;
	outline: none;
`;

const CreditRow = styled.label<{ $disabled: boolean }>`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-top: 18px;
	font-size: 13px;
	opacity: ${props => (props.$disabled ? 0.5 : 1)};

	input {
		width: 18px;
		height: 18px;
		accent-color: ${ui.gold};
	}
`;

const Final = styled.div`
	font-size: 20px;
	font-weight: 600;
	color: ${ui.gold};
`;
